import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ToastService } from '../core/services/toast.service';
import { compressImage, loadStockCover, STOCK_COVER_FILES, stockCoverSrc, stockCoverThumb } from './stock-covers';

export interface CoverOption {
  id: string;
  category: string;
  src: string;
  thumb: string;
}

@Component({
  selector: 'app-cover-image',
  templateUrl: './cover-image.component.html',
  styleUrls: ['./cover-image.component.scss']
})
export class CoverImageComponent implements OnDestroy {

  readonly ALL = 'All';

  private readonly allCovers: CoverOption[] = STOCK_COVER_FILES.map(file => ({
    id: file,
    category: this.toCategory(file),
    src: stockCoverSrc(file),
    thumb: stockCoverThumb(file),
  }));

  readonly categories: string[] = [
    this.ALL,
    ...this.allCovers
      .map(cover => cover.category)
      .filter((category, i, list) => list.indexOf(category) === i),
  ];

  activeCategory: string = this.categories[0];
  covers: CoverOption[] = [];
  selectedCover: CoverOption | null = null;
  imageSrc: string | null = null;
  isCompressing: boolean = false;
  eventName: string = '';
  editEvent: boolean = false;

  private previewUrl: string | null = null;
  private base64Image: string = '';

  constructor(
    private router: Router,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<CoverImageComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.eventName = this.data?.eventName || '';
    this.editEvent = !!localStorage.getItem('editEvent');
    this.covers = this.coversFor(this.activeCategory);

    if (this.isMobile) {
      this.dialogRef.addPanelClass('cover-sheet-panel');
      this.dialogRef.updatePosition({ bottom: '0' });
    }
  }

  ngOnDestroy(): void {
    this.releasePreview();
  }

  get isUploadSelected(): boolean {
    return !!this.imageSrc && !this.selectedCover;
  }

  get canSave(): boolean {
    return !this.isCompressing && (!!this.selectedCover || !!this.base64Image);
  }

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  selectCategory(category: string): void {
    this.activeCategory = category;
    this.covers = this.coversFor(category);
  }

  selectCover(cover: CoverOption): void {
    this.selectedCover = cover;
  }

  trackCover = (_: number, cover: CoverOption) => cover.id;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.toastService.showToast('Please choose an image file (PNG, JPG or WEBP).', 4000);
      input.value = '';
      return;
    }

    this.releasePreview();
    this.previewUrl = URL.createObjectURL(file);
    this.imageSrc = this.previewUrl;
    this.selectedCover = null;
    this.isCompressing = true;
    this.base64Image = '';

    compressImage(file, 0.7)
      .then(compressed => {
        this.isCompressing = false;
        this.base64Image = compressed;
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.isCompressing = false;
        this.resetPick(input);
        this.cdr.detectChanges();
        this.toastService.showToast('That image could not be processed. Please try a different file.', 4000);
      });
  }

  async saveCover(): Promise<void> {
    let payload = this.base64Image;

    if (this.selectedCover) {
      this.isCompressing = true;
      this.cdr.detectChanges();
      try {
        payload = await loadStockCover(this.selectedCover.id);
      } catch {
        this.isCompressing = false;
        this.cdr.detectChanges();
        this.toastService.showToast('That cover could not be loaded. Please try another one.', 4000);
        return;
      }
      this.isCompressing = false;
    }

    if (!payload) return;

    try {
      localStorage.setItem('cover', payload);
    } catch {
      this.cdr.detectChanges();
      this.toastService.showToast('That image is too large to use as a cover. Please try a smaller one.', 4000);
      return;
    }

    localStorage.removeItem('default_image');
    localStorage.removeItem('default_image_url');

    this.dialogRef.close({ coverImage: payload });

    if (this.editEvent) {
      this.router.navigate([this.isMobile ? '/edit-event' : '/event-info']);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private toCategory(file: string): string {
    const name = file.replace(/\d*\.[a-z]+$/i, '');
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  private coversFor(category: string): CoverOption[] {
    return category === this.ALL
      ? this.allCovers
      : this.allCovers.filter(cover => cover.category === category);
  }

  private releasePreview(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }

  private resetPick(input: HTMLInputElement): void {
    this.releasePreview();
    this.imageSrc = null;
    this.base64Image = '';
    input.value = '';
    localStorage.removeItem('cover');
  }
}
